import DeleteIcon from "@mui/icons-material/Delete";
import { Button, Tooltip } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import type { Delivery } from "@types_def/models/delivery.types";
import { UserRank } from "@types_def/models/user.type";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useDataTableSelectionBarContext } from "@/components/ui/data-table-selection-bar";
import { useBulkDeleteDeliveriesMutation } from "@/libs/generated/hooks";

import {
  invalidateCachesForDeliveryIds,
  toastBulkDeliveryResult,
  toastBulkMutationError,
} from "./bulk-mutation-feedback";
import { BulkSelectionConfirmDialog } from "./bulk-selection-confirm-dialog";
import { useBulkSelectionActionVisibility } from "./use-bulk-selection-action-visibility";

export function BulkDeleteAction() {
  const visible = useBulkSelectionActionVisibility({
    requireAdmin: false,
    minRank: UserRank.MAINTAINER,
  });
  const { selectedData, table } = useDataTableSelectionBarContext<Delivery>();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { mutateAsync, isPending } = useBulkDeleteDeliveriesMutation({
    onSuccess: async (data, variables) => {
      await invalidateCachesForDeliveryIds(queryClient, variables.input.ids);
      toastBulkDeliveryResult(
        t,
        data.bulkDeleteDeliveries,
        "network.delivery.bulk.deleteSuccess",
        "network.delivery.bulk.partialOutcome"
      );
      table.resetRowSelection();
      setConfirmOpen(false);
    },
    onError: (error) => {
      toastBulkMutationError(error, t);
    },
  });

  if (!visible) {
    return null;
  }

  const count = selectedData.length;

  return (
    <>
      <Tooltip title={t("delivery.action.delete.title")}>
        <span>
          <Button
            color="error"
            disabled={count === 0 || isPending}
            onClick={() => setConfirmOpen(true)}
            size="small"
            startIcon={<DeleteIcon fontSize="small" />}
            variant="contained"
          >
            {t("delivery.action.delete.text")}
          </Button>
        </span>
      </Tooltip>

      <BulkSelectionConfirmDialog
        alertSeverity="error"
        confirmColor="error"
        confirmLabel={t("delivery.action.delete.text")}
        description={t("delivery.table.bulkConfirm.delete.description", { count })}
        isPending={isPending}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() =>
          mutateAsync({
            input: { ids: selectedData.map((r) => r.id) },
          })
        }
        open={confirmOpen}
        title={t("delivery.table.bulkConfirm.delete.title")}
      />
    </>
  );
}
